

























































































































































































































































































import Component, { mixins } from 'vue-class-component';
import { currencyFormat, loadView, loadWidget } from '@/utils/helpers';
import { namespace } from 'vuex-class';
import {
  IOrganizations,
  IPaginate,
  IPushBulkRequeueResult,
  IPushStateTotal,
  IPushTransaction,
  IStore,
  IUser,
  PayloadState,
  PushState,
} from '@/types/types';
import timestamp from '@/filters/timestamp';
import { Watch } from 'vue-property-decorator';
import WidgetMixins from '@/mixins/WidgetMixins';
import DialogMixins from '@/mixins/DialogMixins';
import qs from 'qs';

const downstreamPushModule = namespace('downstreamPush');
const organizationModule = namespace('organizations');
const authModule = namespace('auth');

@Component({
  name: 'CallbackPushRecovery',
  components: {
    InternetConnection: loadWidget('pages/InternetConnection'),
    ToolBar: loadWidget('widgets/ToolBar'),
    SnackBar: loadWidget('widgets/SnackBar'),
    EmptyPage: loadWidget('pages/EmptyPage'),
    PushResolveDialog: loadView('pages/products/dialogs/PushResolveDialog'),
    PushBulkRequeueDialog: loadView('pages/products/dialogs/PushBulkRequeueDialog'),
  },
  filters: {
    currencyFormat,
    timestamp,
  },
})
export default class CallbackPushRecovery extends mixins(WidgetMixins, DialogMixins) {
  @authModule.Getter('getUserDetails') details!: IUser & IStore;
  @downstreamPushModule.Getter('getTransactions') transactions!: IPushTransaction[];
  @downstreamPushModule.Getter('getPagination') pagination!: IPaginate;
  @downstreamPushModule.Getter('getStateTotals') stateTotals!: IPushStateTotal[];
  @downstreamPushModule.Getter('getMaxPushAttempts') maxPushAttempts!: number;
  @downstreamPushModule.Getter('getBulkResult') bulkResult!: IPushBulkRequeueResult | null;
  @organizationModule.Getter('getAllOrganizations') organizations!: IOrganizations[];

  options: any = {};
  query: string = '';
  pushState: PushState = 'EXHAUSTED';
  organizationId: string | null = null;
  dateRangeMenu: boolean = false;
  dateRange: string[] = [];
  search: string = '';
  expanded: IPushTransaction[] = [];
  resolvingTransaction: IPushTransaction | null = null;

  stateOptions: Array<{ text: string; value: PushState }> = [
    { text: 'EXHAUSTED — needs manual requeue', value: 'EXHAUSTED' },
    { text: 'STALLED — orphaned in-progress claim', value: 'STALLED' },
    { text: 'RETRYING — sweep will handle it', value: 'RETRYING' },
    { text: 'UNRESOLVED — everything not pushed', value: 'UNRESOLVED' },
    { text: 'PUSHED — completed', value: 'PUSHED' },
  ];

  headers: Array<{ text: string; value: string; align?: string; sortable?: boolean }> = [
    { text: 'Created', value: 'createdAt' },
    { text: 'Reference', value: 'reference' },
    { text: 'Organization', value: 'organizationName' },
    { text: 'Account', value: 'accountNumber' },
    { text: 'Amount', value: 'totalAmount', align: 'end' },
    { text: 'Attempts', value: 'attempts', align: 'end' },
    { text: 'State', value: 'state' },
    { text: '', value: 'actions', sortable: false },
    { text: '', value: 'data-table-expand' },
  ];

  get canBulkRequeue(): boolean {
    return this.pushState === 'EXHAUSTED' || this.pushState === 'STALLED';
  }

  get dateRangeLabel(): string {
    const range = [...this.dateRange].sort();
    if (range.length === 0) return '';
    if (range.length === 1) return `from ${range[0]}`;
    return `${range[0]} — ${range[1]}`;
  }

  get emptySubtitle(): string {
    const subtitles: { [key in PushState]: string } = {
      EXHAUSTED: 'No transactions have run out of push attempts — the sweep is keeping up',
      STALLED: 'No orphaned in-progress claims',
      RETRYING: 'Nothing is waiting on the sweep right now',
      UNRESOLVED: 'Every completed transaction has been pushed downstream',
      PUSHED: 'No pushed transactions match the current filters',
    };
    return subtitles[this.pushState];
  }

  timestampOf(value: string): string {
    return timestamp(value, 'DD/MM/YYYY HH:mm');
  }

  stateClass(state: PushState): string {
    const classes: { [key in PushState]: string } = {
      EXHAUSTED: 'ka-status--error',
      STALLED: 'ka-status--warning',
      RETRYING: 'ka-status--info',
      UNRESOLVED: 'ka-status--info',
      PUSHED: 'ka-status--success',
    };
    return classes[state] || 'ka-status--info';
  }

  statIconClass(state: PushState): string {
    const classes: { [key in PushState]: string } = {
      EXHAUSTED: 'ka-stat__icon--error',
      STALLED: 'ka-stat__icon--warning',
      RETRYING: 'ka-stat__icon--info',
      UNRESOLVED: 'ka-stat__icon--info',
      PUSHED: 'ka-stat__icon--success',
    };
    return classes[state] || 'ka-stat__icon--info';
  }

  statIcon(state: PushState): string {
    const icons: { [key in PushState]: string } = {
      EXHAUSTED: 'error_outline',
      STALLED: 'hourglass_disabled',
      RETRYING: 'autorenew',
      UNRESOLVED: 'pending_actions',
      PUSHED: 'task_alt',
    };
    return icons[state] || 'sync';
  }

  buildFilter(): { [key: string]: any } {
    const range = [...this.dateRange].sort();
    return {
      state: this.pushState,
      organizationId: this.organizationId,
      ...(range[0] && { from: range[0] }),
      ...(range[1] && { to: `${range[1]}T23:59:59.999Z` }),
      ...(this.search && { search: this.search }),
    };
  }

  buildQuery(page: number = 1, limit: number = 50): string {
    return qs.stringify({ page, limit, ...this.buildFilter() }, { skipNulls: true, addQueryPrefix: true });
  }

  fetchTransactions(): void {
    this.$store.dispatch('downstreamPush/fetchTransactions', this.query);
  }

  refreshFromFilters(): void {
    this.query = this.buildQuery(1, this.options?.itemsPerPage || 50);
    this.fetchTransactions();
  }

  applySearch(): void {
    this.refreshFromFilters();
  }

  clearSearch(): void {
    this.search = '';
    this.refreshFromFilters();
  }

  openResolveDialog(transaction: IPushTransaction): void {
    this.resolvingTransaction = transaction;
    this.open('resolve');
  }

  handleResolve(payload: {
    action: 'requeue' | 'mark-pushed';
    transactionId: string;
    reason?: string;
    confirmedNotPushed?: boolean;
  }): void {
    this.$store.dispatch('downstreamPush/resolveTransaction', {
      ...payload,
      adminId: this.details?.id ?? this.details?.email,
      query: this.query,
    });
  }

  openBulkRequeue(): void {
    this.$store.dispatch('downstreamPush/clearBulkResult');
    this.open('bulkRequeue');
  }

  closeBulkRequeue(payload: PayloadState): void {
    this.$store.dispatch('downstreamPush/clearBulkResult');
    this.close(payload);
  }

  runBulkRequeue(payload: { reason?: string }, dryRun: boolean): void {
    this.$store.dispatch('downstreamPush/bulkRequeue', {
      adminId: this.details?.id ?? this.details?.email,
      body: {
        filter: this.buildFilter(),
        reason: payload.reason,
        dryRun,
      },
      query: this.query,
    });
  }

  @Watch('pushState')
  onPushStateChange(): void {
    this.refreshFromFilters();
  }

  @Watch('organizationId')
  onOrganizationIdChange(): void {
    this.refreshFromFilters();
  }

  @Watch('dateRange')
  onDateRangeChange(): void {
    // Wait for the second date; a single date is applied as an open range.
    if (this.dateRange.length === 2) this.dateRangeMenu = false;
    this.refreshFromFilters();
  }

  @Watch('options', { deep: true })
  onOptionsChange(paginate: IPaginate): void {
    const query = this.buildQuery(paginate.page, paginate.itemsPerPage);
    if (query === this.query) return;
    this.query = query;
    this.fetchTransactions();
  }

  created(): void {
    this.$store.dispatch('auth/me');
    this.$store.dispatch('organizations/list', false);
    this.query = this.buildQuery();
    this.fetchTransactions();
  }
}
